/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface SystemTimerProps {
  id: string;
  name: string;
  description: string;
  commandName: string;
  runIntervalInSeconds: number;
  hasRunCondition: boolean;
  isEnqueued: boolean;
  lastRunDate: Date | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class SystemTimerRecord extends Record<SystemTimerProps>({
  id: null as any,
  name: null as any,
  description: null as any,
  commandName: null as any,
  runIntervalInSeconds: null as any,
  hasRunCondition: null as any,
  isEnqueued: null as any,
  lastRunDate: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    description: string;
    commandName: string;
    runIntervalInSeconds: number;
    hasRunCondition: boolean;
    isEnqueued: boolean;
    lastRunDate: string | number | Date | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): SystemTimerRecord {
    if (json == null) {
      throw new Error('SystemTimerRecord.fromJS: parameter cannot be null');
    }
    return new SystemTimerRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'SystemTimerJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'SystemTimerJson.name'),
      description: getFromOrThrow(json, (j) => j.description, 'SystemTimerJson.description'),
      commandName: getFromOrThrow(json, (j) => j.commandName, 'SystemTimerJson.commandName'),
      runIntervalInSeconds: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'runIntervalInSeconds', 0),
        'SystemTimerJson.runIntervalInSeconds',
      ),
      hasRunCondition: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'hasRunCondition', false),
        'SystemTimerJson.hasRunCondition',
      ),
      isEnqueued: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isEnqueued', false),
        'SystemTimerJson.isEnqueued',
      ),
      lastRunDate: json.lastRunDate != null ? new Date(json.lastRunDate) : null,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    description: string;
    commandName: string;
    runIntervalInSeconds: number;
    hasRunCondition: boolean;
    isEnqueued: boolean;
    lastRunDate: string | number | Date | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [SystemTimerRecord, Error[]] {
    if (json == null) {
      throw new Error('SystemTimerRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new SystemTimerRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'SystemTimerJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'SystemTimerJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      description: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.description, 'SystemTimerJson.description'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      commandName: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.commandName, 'SystemTimerJson.commandName'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      runIntervalInSeconds: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'runIntervalInSeconds', 0),
            'SystemTimerJson.runIntervalInSeconds',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      hasRunCondition: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'hasRunCondition', false),
            'SystemTimerJson.hasRunCondition',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isEnqueued: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isEnqueued', false),
            'SystemTimerJson.isEnqueued',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastRunDate: tryOrDefault(
        () => (json.lastRunDate != null ? new Date(json.lastRunDate) : null),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: SystemTimerProps) {
    super(props);
    if (props == null) {
      throw new Error('SystemTimerRecord constructor parameter cannot be null');
    }
  }
}

export abstract class SystemTimerViewModelBase implements SystemTimerProps {
  protected readonly _props: SystemTimerProps;
  private _json?: SystemTimerProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get description(): string {
    return this._props.description;
  }
  get commandName(): string {
    return this._props.commandName;
  }
  get runIntervalInSeconds(): number {
    return this._props.runIntervalInSeconds;
  }
  get hasRunCondition(): boolean {
    return this._props.hasRunCondition;
  }
  get isEnqueued(): boolean {
    return this._props.isEnqueued;
  }
  get lastRunDate(): Date | null {
    return this._props.lastRunDate;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: SystemTimerProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        description: this._props.description,
        commandName: this._props.commandName,
        runIntervalInSeconds: this._props.runIntervalInSeconds,
        hasRunCondition: this._props.hasRunCondition,
        isEnqueued: this._props.isEnqueued,
        lastRunDate: this._props.lastRunDate,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllSystemTimerEntities = (state: AppState) =>
  state.entityStores.systemTimer.entities;

export const selectSystemTimerById = (id: string) => (state: AppState) =>
  state.entityStores.systemTimer.entities.get(id);

export const selectSystemTimerMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.systemTimer.meta.get(id);

export const selectSystemTimerAndMetadataById = (id: string) =>
  createSelector(
    selectSystemTimerById(id),
    selectSystemTimerMetadataById(id),
    (systemTimer, meta) => ({ systemTimer, meta }),
  );

export function selectSystemTimerEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllSystemTimerEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, SystemTimerRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectSystemTimerEntitiesByName(name: string) {
  return createSelector(selectAllSystemTimerEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectSystemTimerEntitiesByDescription(description: string) {
  return createSelector(selectAllSystemTimerEntities, (entities) =>
    entities.filter((e) => e.description === description).valueSeq(),
  );
}

export function selectSystemTimerEntitiesByCommandName(commandName: string) {
  return createSelector(selectAllSystemTimerEntities, (entities) =>
    entities.filter((e) => e.commandName === commandName).valueSeq(),
  );
}

export function selectSystemTimerEntitiesByRunIntervalInSeconds(runIntervalInSeconds: number) {
  return createSelector(selectAllSystemTimerEntities, (entities) =>
    entities.filter((e) => e.runIntervalInSeconds === runIntervalInSeconds).valueSeq(),
  );
}

export function selectSystemTimerEntitiesByHasRunCondition(hasRunCondition: boolean) {
  return createSelector(selectAllSystemTimerEntities, (entities) =>
    entities.filter((e) => e.hasRunCondition === hasRunCondition).valueSeq(),
  );
}

export function selectSystemTimerEntitiesByIsEnqueued(isEnqueued: boolean) {
  return createSelector(selectAllSystemTimerEntities, (entities) =>
    entities.filter((e) => e.isEnqueued === isEnqueued).valueSeq(),
  );
}

export function selectSystemTimerEntitiesByLastRunDate(lastRunDate: Date) {
  return createSelector(selectAllSystemTimerEntities, (entities) =>
    entities.filter((e) => e.lastRunDate === lastRunDate).valueSeq(),
  );
}
//#endregion Selectors
